import { useCallback, useState } from "react";
import { useMapRender } from "../../hooks/map/useMapRender";
import Modal from "../common/Modal";
import Input from "./components/Input";
import Toggle from "../common/Toggle";
import Button from "../common/Button";
import toast from "react-hot-toast";
import request from "../../utils/fetch";

function DialogContent({
  tract,
  newPoint,
  existingPoints,
  onPick,
  isLoading,
  name,
  setName,
  irrigated,
  setIrrigated,
  note,
  setNote,
  onSave,
  isAddMode,
  setIsAddMode,
  activeIndex,
  setActiveIndex,
}) {
  const newSoil = newPoint
    ? {
        geometry: {
          type: "Point",
          coordinates: [newPoint.lng, newPoint.lat],
        },
        _id: "soil_location",
      }
    : undefined;

  const activePoint = activeIndex != null ? existingPoints?.[activeIndex] : undefined;

  const { renderMap } = useMapRender({
    tracts: [tract],
    soils: isAddMode ? (newSoil ? [newSoil] : []) : existingPoints ?? [],
    mapComponent: "soil",
    selectedItem: isAddMode ? newSoil : activePoint,
    extraMapProps: {
      expandable: true,
      onIconClick: (_, id, location) => {
        if (isAddMode) {
          onPick(id, location);
        } else {
          const index = existingPoints?.findIndex((p) => p._id === id);
          if (index >= 0) {
            setActiveIndex(index);
          }
        }
      },
    },
  });

  return (
    <>
      <div>
        <div className="flex gap-4 items-center">
          {isAddMode ?
            <Input
              label="Sample Location Name"
              name="create-soil-report-name"
              value={name}
              onChange={setName}
            />
            : <p className="flex flex-col flex-1">
                <span>Sample Location Name</span>
                <span className="p-1.5 border border-transparent">
                  {activePoint?.SoilLocationID || <>&nbsp;</>}
                </span>
            </p>
          }
          <Toggle
            label="Irrigated"
            name="create-soil-report-irrigated"
            value={isAddMode ? irrigated : activePoint?.Irrigated}
            onChange={setIrrigated}
            disabled={!isAddMode}
          />
        </div>
        <Input
          label="Note"
          name="create-soil-report-note"
          type="textarea"
          value={isAddMode ? note : activePoint?.Notes}
          onChange={setNote}
          disabled={!isAddMode}
        />
      </div>
      <div className="w-4/6 aspect-video mx-auto">
        <div className="w-full flex justify-between items-center py-1 gap-1">
          <span>
            {isAddMode ?
              'Click a point on the map to select the location of the sample' :
              'Select a known sample location from the map'
            }
          </span>
          <Button onClick={() => setIsAddMode(!isAddMode)}>
            {isAddMode ? 'Cancel' : 'Add Sample Location'}
          </Button>
        </div>
        {renderMap()}
      </div>
      <div className="w-full flex justify-end mt-1">
        <Button
          onClick={onSave}
          disabled={isAddMode ? (!name || !newPoint) : !activePoint}
          loading={isLoading}
        >
          {isAddMode ? "Save and Continue" : "Continue"}
        </Button>
      </div>
    </>
  );
}

// TODO: This modal and the content need a lot of work, for now I'm keeping it
//       similar to the original version for the sake of getting this working.
export default function CreateSoilReportDialog({
  open,
  onClose,
  tract,
  locations,
  refreshLocations,
}) {
  const [isAddMode, setIsAddMode] = useState(false);

  const [activeIndex, setActiveIndex] = useState(undefined);

  const [name, setName] = useState('');
  const [irrigated, setIrrigated] = useState(false);
  const [note, setNote] = useState('');

  const [newPoint, setNewPoint] = useState(undefined);

  const [isLoading, setIsLoading] = useState(false);

  const internalOnClose = useCallback((selectedLocationId) => {
    // Clear the form.
    setName('');
    setIrrigated(false);
    setNote('');
    setActiveIndex(undefined);
    setNewPoint(undefined);
    setIsAddMode(false);

    onClose(selectedLocationId);
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    if (isAddMode) {
      setIsLoading(true);

      request({
        method: "post",
        url: "/soils",
        data: {
          irrigated,
          soil_location_id: name,
          notes: note,
          associated_tract: newPoint.id,
          latitude: parseFloat(newPoint.lat),
          longitude: parseFloat(newPoint.lng),
        },
      }).then((res) => {
        refreshLocations().then(() => {
          internalOnClose(res);
        });
      }).catch((e) => {
        toast.error(e.response?.data?.message || e.message || 'Something went wrong');
      }).finally(() => {
        setIsLoading(false);
      });
    } else {
      internalOnClose(locations[activeIndex]?._id);
    }
  }, [name, irrigated, note, newPoint, internalOnClose, locations, activeIndex]);

  return (
    <Modal
      isOpen={open}
      close={() => internalOnClose()}
      title={isAddMode ? "New Sample Location" : "Choose Sample Location"}
      Content={DialogContent}
      // FIXME: This is how the old version worked, but it feels hacky and bad.
      contentClassName="soilLocationModalContent"
      contentProps={{
        tract,
        newPoint,
        existingPoints: locations,
        isLoading,
        onPick: (id, latLng) => setNewPoint({ id, ...latLng }),
        onSave: handleSubmit,
        name, setName,
        irrigated, setIrrigated,
        note, setNote,
        isAddMode, setIsAddMode,
        activeIndex, setActiveIndex,
      }}
    />
  )
}
